import React, { useState, useEffect } from "react";
import { useGameLogic } from "./hooks/useGameLogic";
import { formatMoney } from "./utils/helpers";
import LoginPage from "./pages/LoginPage";
import LoadingPage from "./pages/LoadingPage";
import LobbyPage from "./pages/LobbyPage";
import GamePage from "./pages/GamePage";
import VictoryPage from "./pages/VictoryPage";
import RoundResumePage from "./pages/RoundResumePage";
import AdminLoginPage from "./pages/AdminLoginPage";
import AdminPage from "./pages/AdminPage";
import { onSnapshot, doc } from "firebase/firestore";
import { db } from "./firebase";
import { GAME_ID } from "./utils/helpers";

function App() {
  const [isAdminMode, setIsAdminMode] = useState(false);
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  const {
    playerId,
    playerName,
    setPlayerName,
    playerData,
    gameData,
    topBids,
    bidAmount,
    setBidAmount,
    statusMsg,
    pollVote,
    isSubmittingVote,
    timeLeft,
    canPlay,
    handleJoin,
    awardMoney,
    handleBid,
    handlePollVote,
    totalPlayers,
    totalVotes,
    isProcessingRound,
    allPlayers,
    isSpectator,
    isShowingResume,
  } = useGameLogic();

  // Detectar si la URL contiene /admin
  useEffect(() => {
    if (window.location.pathname.includes("/admin")) {
      setIsAdminMode(true);
    }
  }, []);

  // Comprobar autenticación de admin guardada
  useEffect(() => {
    const adminAuth = sessionStorage.getItem("adminAuth");
    if (adminAuth === "true") {
      setIsAdminAuthenticated(true);
    }
  }, []);

  const handleAdminLogin = () => {
    setIsAdminAuthenticated(true);
    sessionStorage.setItem("adminAuth", "true");
  };

  const handleAdminLogout = () => {
    setIsAdminAuthenticated(false);
    sessionStorage.removeItem("adminAuth");
    setIsAdminMode(false);
    window.location.href = "/";
  };

  // Detectar cuando Firebase Hosting actualiza los archivos
  useEffect(() => {
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        // Nueva versión detectada, forzar recarga
        window.location.reload();
      });
    }

    // También puedes usar un campo de versión en Firestore
    const unsubscribe = onSnapshot(doc(db, "games", GAME_ID), (docSnap) => {
      const data = docSnap.data();

      // Si el admin actualiza un campo "appVersion", todos recargan
      if (
        data?.forceReload &&
        data.forceReload !== localStorage.getItem("lastReload")
      ) {
        localStorage.setItem("lastReload", data.forceReload);
        window.location.reload();
      }
    });

    return () => unsubscribe();
  }, []);

  // Modo administrador
  if (isAdminMode) {
    if (!isAdminAuthenticated) {
      return <AdminLoginPage onLogin={handleAdminLogin} />;
    }
    return <AdminPage onLogout={handleAdminLogout} />;
  }

  // Pantalla de login
  // Mostrar login si no hay playerId O si hay playerId pero no hay playerData aún
  if (!playerId) {
    return (
      <LoginPage
        playerName={playerName}
        setPlayerName={setPlayerName}
        handleJoin={handleJoin}
      />
    );
  }

  // Si es espectador, mostrar lobby con información del juego
  if (isSpectator && gameData) {
    return (
      <LobbyPage
        playerName={playerName}
        players={allPlayers.filter((p) => p.active !== false)} // Mostrar solo jugadores activos
        timeLeft={timeLeft}
        isSpectator={true}
        currentRound={gameData.currentRound || 1}
        topBids={topBids}
        gameData={gameData}
        allPlayers={allPlayers}
      />
    );
  }

  // Pantalla de carga (cuando tiene playerId pero está esperando datos de Firebase)
  if (!playerData || !gameData) {
    return <LoadingPage />;
  }

  // Jugador eliminado (sin pan) - mostrar lobby como espectador
  if (playerData.active === false && gameData.roundStatus !== "finished") {
    return (
      <LobbyPage
        playerName={playerName}
        players={allPlayers.filter((p) => p.active !== false)} // Mostrar solo jugadores activos
        timeLeft={timeLeft}
        isSpectator={true}
        currentRound={gameData.currentRound || 1}
        isEliminated={true}
        topBids={topBids}
        gameData={gameData}
        allPlayers={allPlayers}
      />
    );
  }

  // Pantalla de lobby (cuando el juego aún no ha comenzado)
  if (gameData.roundStatus === "waiting") {
    return (
      <LobbyPage
        playerName={playerName}
        players={allPlayers}
        timeLeft={timeLeft}
        topBids={topBids}
        gameData={gameData}
        allPlayers={allPlayers}
      />
    );
  }

  // Pantalla de resumen de ronda (mostrando resultados de la ronda anterior)
  if (isShowingResume && gameData.roundStatus === "resume") {
    return (
      <RoundResumePage
        currentRound={gameData.currentRound}
        timeLeft={timeLeft}
      />
    );
  }

  // Pantalla de victoria (juego terminado)
  if (gameData.roundStatus === "finished") {
    return <VictoryPage />;
  }

  // Pantalla de procesamiento de ronda (loading)
  if (isProcessingRound) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "20px",
        }}
      >
        <div
          style={{
            background: "white",
            borderRadius: "20px",
            padding: "60px 40px",
            boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
            textAlign: "center",
            maxWidth: "500px",
          }}
        >
          <div
            style={{
              width: "80px",
              height: "80px",
              border: "8px solid #f3f3f3",
              borderTop: "8px solid #667eea",
              borderRadius: "50%",
              margin: "0 auto 30px",
              animation: "spin 1s linear infinite",
            }}
          />
          <h2
            style={{
              fontSize: "28px",
              color: "#333",
              marginBottom: "15px",
              fontWeight: "bold",
            }}
          >
            Procesando ronda...
          </h2>
          <p
            style={{
              fontSize: "16px",
              color: "#666",
            }}
          >
            Asignando panes, distribuyendo becas y preparando siguiente ronda...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Pantalla del juego
  return (
    <GamePage
      playerData={playerData}
      gameData={gameData}
      timeLeft={timeLeft}
      canPlay={canPlay}
      awardMoney={awardMoney}
      bidAmount={bidAmount}
      setBidAmount={setBidAmount}
      handleBid={handleBid}
      topBids={topBids}
      handlePollVote={handlePollVote}
      isSubmittingVote={isSubmittingVote}
      pollVote={pollVote}
      statusMsg={statusMsg}
      totalPlayers={totalPlayers}
      totalVotes={totalVotes}
      allPlayers={allPlayers}
    />
  );
}

export default App;
